// The examples (samples) of a package: each one is a payload and the
// values it must decode to, written in the decoder's result shape. For
// scenario C they are the specification decode.js must meet; for a protobuf
// type (A) the expected values can be taken from the current decoding. The
// store engine runs every one; the assistant's edge cases are marked as its own.
import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "./icons";
import type { Actions, WorkbenchData } from "./model";
import type { Example, SampleResult } from "../lib/types";
import { fmtNumber, t } from "../i18n";

const TEMPLATE = `{
  "state": { "temperature": 23.45 },
  "timeseries": [ { "key": "temperature", "value": 23.45 } ],
  "events": [],
  "config": {}
}`;

const blank: Example = { name: "", hex: "", expected: undefined, origin: "person" };

// Text payloads (CSV, JSON, AT-style lines) are typed as text and stored as
// their UTF-8 bytes in hex; hex that decodes to printable text opens as text.
const toHex = (t: string) => [...new TextEncoder().encode(t)].map((v) => v.toString(16).padStart(2, "0")).join(" ");
function hexToText(hex: string): string | null {
  const clean = hex.replace(/\s+/g, "");
  if (clean === "" || clean.length % 2 || /[^0-9a-f]/i.test(clean)) return null;
  const b = new Uint8Array(clean.match(/../g)!.map((x) => parseInt(x, 16)));
  try {
    const t = new TextDecoder("utf-8", { fatal: true }).decode(b);
    return /^[^\x00-\x08\x0b\x0c\x0e-\x1f\x7f]*$/.test(t) ? t : null;
  } catch {
    return null;
  }
}

export function ExamplesView({ data, actions, writer }: { data: WorkbenchData; actions: Actions; writer: boolean }) {
  const [list, setList] = useState<Example[] | null>(null);
  const [results, setResults] = useState<Record<string, SampleResult>>({});
  const [sel, setSel] = useState<string>("");
  const protobuf = data.project.scenario !== "C";
  // protocol logic type (GIOTHUB-31): an example is a result document of the logic module
  const logic = !!data.logic;
  const moduleExamples = data.modules?.find((m) => m.id === data.logic?.module)?.examples ?? [];
  const [form, setForm] = useState({ name: "", hex: "", result: "", received_at: "", topic: "", device_serial: "", device_id: "", expected: TEMPLATE, fromDecoder: protobuf });
  const binInput = useRef<HTMLInputElement>(null);
  const [asText, setAsText] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const run = () => actions.runExamples(true).then((rs) => setResults(Object.fromEntries(rs.map((r) => [r.name, r])))).catch(() => {});
  const load = () =>
    actions.examples().then((l) => {
      setList(l);
      return l;
    });
  useEffect(() => {
    load()
      .then((l) => {
        // ?example=<name> opens that example (links from the manual)
        const want = new URLSearchParams(location.search).get("example");
        if (!sel && l.length > 0 && !form.hex) open(l.find((e) => e.name === want) ?? l[0]);
      })
      .catch((e) => setErr((e as Error).message));
    run();
  }, [data.project.head_rev, data.items]);

  const open = (ex: Example) => {
    setSel(ex.name);
    setErr("");
    setAsText(hexToText(ex.hex) !== null);
    setForm({ name: ex.name, hex: ex.hex, result: ex.result !== undefined ? JSON.stringify(ex.result, null, 2) : "",
      received_at: ex.received_at ?? "", topic: ex.topic ?? "", device_serial: ex.device_serial ?? "", device_id: ex.device_id ?? "",
      expected: JSON.stringify(ex.expected, null, 2), fromDecoder: false });
  };
  const fresh = () => {
    setSel("");
    setErr("");
    setForm({ name: "", hex: "", result: "", received_at: "", topic: "", device_serial: "", device_id: "", expected: TEMPLATE, fromDecoder: protobuf });
  };
  const current = list?.find((e) => e.name === sel);
  const readOnly = !writer || current?.origin === "ai";
  const retake = async () => {
    if (!current) return;
    setBusy("retake");
    setErr("");
    try {
      await actions.putExample({ ...current, expected: undefined, from_decoder: true });
      await load();
      await run();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  const res = sel ? results[sel] : undefined;

  const save = async () => {
    let expected: unknown;
    if (!form.fromDecoder) {
      try {
        expected = JSON.parse(form.expected);
      } catch (e) {
        setErr(t("ex.badjson.expected", { error: (e as Error).message }));
        return;
      }
    }
    let result: unknown;
    if (logic) {
      try {
        result = JSON.parse(form.result);
      } catch (e) {
        setErr(t("ex.badjson.result", { error: (e as Error).message }));
        return;
      }
    }
    setBusy("save");
    setErr("");
    try {
      await actions.putExample({ ...blank, name: form.name.trim(), hex: logic ? "" : form.hex, result, received_at: form.received_at || undefined,
        topic: form.topic || undefined, device_serial: form.device_serial || undefined,
        device_id: (form.device_serial && form.device_id) || undefined, expected, from_decoder: form.fromDecoder || undefined });
      await load();
      setSel(form.name.trim());
      await run();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  };

  const passing = list?.filter((e) => results[e.name]?.ok).length ?? 0;
  return (
    <div class="flex min-h-0 flex-1">
      <aside class="flex w-[300px] flex-none flex-col border-r border-line bg-side">
        <div class="flex h-11 flex-none items-center gap-2 border-b border-line px-3">
          <span class="text-[13px] font-semibold">{t("ex.title")}</span>
          <span class="text-[12px] text-muted">{list ? t("ex.pass", { passing, total: list.length }) : ""}</span>
          <span class="flex-1" />
          <button type="button" class="btn h-7 px-2 text-[12px]" onClick={run} title={t("ex.run.title")}>{t("ex.run")}</button>
          {writer && <button type="button" class="btn h-7 w-7 justify-center px-0" aria-label={t("ex.new")} onClick={fresh}><Icon name="plus" size={14} stroke={2.2} /></button>}
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto py-1.5">
          {list?.length === 0 && <p class="px-3 text-[12.5px] text-dim">{t("ex.empty")}</p>}
          {list?.map((e) => {
            const r = results[e.name];
            return (
              <button type="button" onClick={() => open(e)} class={`tree h-auto w-full py-1.5 text-left ${sel === e.name ? "tree-sel" : ""}`}>
                <Icon name={!r ? "info" : r.ok ? "check" : "warn"} size={14} stroke={2.2} class={!r ? "text-dim" : r.ok ? "text-good" : "text-warn"} />
                <span class="min-w-0 flex-1">
                  <span class="block truncate font-mono text-[12.5px]">{e.name}</span>
                  {e.note && <span class="block truncate text-[11.5px] text-dim">{e.note}</span>}
                </span>
                {e.origin === "ai" && <span class="pill h-4 bg-violet-soft px-1.5 text-violet">{t("ex.ai")}</span>}
              </button>
            );
          })}
        </div>
      </aside>
      <section class="min-w-0 flex-1 overflow-y-auto">
        <div class="mx-auto flex max-w-[980px] flex-col gap-4 px-6 py-5">
          <div class="flex items-center gap-3">
            <h2 class="m-0 text-[17px] font-semibold">{sel ? sel : t("ex.new")}</h2>
            {current?.origin === "ai" && <span class="pill bg-violet-soft text-violet">{t("ex.byai")}</span>}
            <span class="flex-1" />
            <a href={logic ? "/help/logic-types#examples" : "/help/scenario-c#writing-examples"} target="_blank" class="text-[12.5px]">{t("ex.howto")}</a>
          </div>
          {current?.note && <p class="m-0 text-muted">{current.note}</p>}
          {current?.from_decoder && (
            <p class="m-0 flex flex-wrap items-center gap-3 text-[13px] text-warn">{t("ex.fromdecoder.note")}
              {writer && <button type="button" class="btn h-7 px-2 text-[12px] font-normal" disabled={!!busy}
                title={t("ex.retake.title")}
                onClick={retake}>{busy === "retake" ? t("ex.decoding") : t("ex.retake")}</button>}
            </p>
          )}
          {current?.message !== undefined && current?.message !== null && (
            <details class="text-[13px]"><summary class="cursor-pointer text-muted">{t("ex.message")}</summary>
              <pre class="mb-0 mt-1 overflow-x-auto rounded-lg border border-line2 bg-side px-3 py-2 font-mono text-[12px] text-ink2">{JSON.stringify(current.message, null, 2)}</pre></details>
          )}
          <fieldset class="m-0 grid grid-cols-1 gap-3 border-0 p-0 md:grid-cols-[1fr_1.2fr_1fr]" disabled={readOnly}>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("ex.name")}</span>
              <input class="field font-mono" value={form.name} disabled={!!sel} placeholder="measurement" pattern="[a-z0-9][a-z0-9_\-]*"
                onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("ex.receivedat")}</span>
              <input class="field font-mono" value={form.received_at} placeholder="2026-10-06T10:00:00Z"
                onInput={(e) => setForm({ ...form, received_at: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("ex.serial")}</span>
              <input class="field font-mono" value={form.device_serial} placeholder={t("ex.serial.ph")} onInput={(e) => setForm({ ...form, device_serial: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1 md:col-span-3">
              <span class="text-[12px] font-semibold text-ink2">{t("ex.topic")}</span>
              <input class="field font-mono" value={form.topic} placeholder={t("ex.topic.ph")} onInput={(e) => setForm({ ...form, topic: (e.target as HTMLInputElement).value })} />
            </label>
            {logic && (
              <label class="flex flex-col gap-1 md:col-span-3">
                <span class="flex items-center gap-3 text-[12px] font-semibold text-ink2">{t("ex.result")}
                  {!readOnly && moduleExamples.length > 0 && (
                    <select class="field h-6 w-auto py-0 text-[11.5px] font-normal" value="" aria-label={t("ex.module.aria")}
                      onChange={(e) => {
                        const x = moduleExamples.find((m) => m.name === (e.target as HTMLSelectElement).value);
                        if (x) setForm((f) => ({ ...f, result: JSON.stringify(x.result, null, 2), name: f.name || `module_${x.name}` }));
                      }}>
                      <option value="">{t("ex.module.pick")}</option>
                      {moduleExamples.map((m) => <option value={m.name}>{m.name}</option>)}
                    </select>
                  )}
                </span>
                <textarea class="field h-auto py-1.5 font-mono text-[12.5px] leading-[1.55]" rows={10} spellcheck={false} value={form.result}
                  placeholder={'{ "v": 1, "use_case": "TSI", "samples": [ ... ] }'} aria-label={t("ex.result.aria")}
                  onInput={(e) => setForm({ ...form, result: (e.target as HTMLTextAreaElement).value })} />
                <span class="text-[12px] text-muted">{t("ex.result.hint")}</span>
              </label>
            )}
            <label class={`flex flex-col gap-1 md:col-span-3 ${logic ? "hidden" : ""}`}>
              <span class="flex items-center gap-3 text-[12px] font-semibold text-ink2">{t("ex.payload")}
                <span class="inline-flex overflow-hidden rounded-md border border-line2 font-normal" role="group" aria-label={t("ex.payload.format")}>
                  {(["hex", "text"] as const).map((m) => (
                    <button type="button" class={`h-6 px-2 text-[11.5px] ${(m === "text") === asText ? "bg-sel text-ink" : "text-muted"}`}
                      aria-pressed={(m === "text") === asText} title={m === "text" ? t("ex.payload.text.title") : t("ex.payload.hex.title")}
                      onClick={() => {
                        if (m === "text" && !asText && form.hex.trim() && hexToText(form.hex) === null) {
                          setErr(t("ex.payload.nottext"));
                          return;
                        }
                        setErr("");
                        setAsText(m === "text");
                      }}>{m === "hex" ? t("ex.payload.hex") : t("ex.payload.text")}</button>
                  ))}
                </span>
                {!readOnly && <button type="button" class="btn h-6 px-2 text-[11.5px] font-normal" onClick={() => binInput.current?.click()}>{t("ex.loadbin")}</button>}
                <input ref={binInput} type="file" class="hidden" onChange={async (e) => {
                  const el = e.target as HTMLInputElement;
                  const f = el.files?.[0];
                  el.value = "";
                  if (!f) return;
                  const b = new Uint8Array(await f.arrayBuffer());
                  const hex = [...b].map((v) => v.toString(16).padStart(2, "0")).join(" ");
                  setAsText(hexToText(hex) !== null);
                  setForm((x) => ({ ...x, hex,
                    name: x.name || f.name.replace(/\.[^.]+$/, "").toLowerCase().replace(/[^a-z0-9_-]+/g, "_") }));
                }} />
              </span>
              {asText ? (
                <textarea class="field h-auto py-1.5 font-mono" rows={2} value={hexToText(form.hex) ?? ""} placeholder="42.664289,23.315863,27.6"
                  aria-label={t("ex.payload.text.aria")} onInput={(e) => setForm({ ...form, hex: toHex((e.target as HTMLTextAreaElement).value) })} />
              ) : (
                <textarea class="field h-auto py-1.5 font-mono" rows={2} value={form.hex} placeholder="01 00 09 29 37 0e 10"
                  aria-label={t("ex.payload.hex.aria")} onInput={(e) => setForm({ ...form, hex: (e.target as HTMLTextAreaElement).value })} />
              )}
              {!asText && hexToText(form.hex) !== null && <span class="text-[12px] text-muted">{t("ex.astext")} <span class="font-mono">{hexToText(form.hex)}</span></span>}
            </label>
            {protobuf && !sel && (
              <label class="flex items-start gap-2.5 text-[13px] md:col-span-3">
                <input type="checkbox" class="mt-1" checked={form.fromDecoder} onChange={() => setForm({ ...form, fromDecoder: !form.fromDecoder })} />
                <span>{t("ex.fromdecoder")}
                  <span class="block text-[12px] text-muted">{t("ex.fromdecoder.hint")}</span></span>
              </label>
            )}
            {!form.fromDecoder && (
              <label class="flex flex-col gap-1 md:col-span-3">
                <span class="text-[12px] font-semibold text-ink2">{t("ex.expected")}</span>
                <textarea class="field h-auto py-1.5 font-mono text-[12.5px] leading-[1.55]" rows={14} spellcheck={false} value={form.expected}
                  onInput={(e) => setForm({ ...form, expected: (e.target as HTMLTextAreaElement).value })} />
              </label>
            )}
          </fieldset>
          {err && <p class="m-0 text-[13px] text-crit">{err}</p>}
          {writer && (
            <div class="flex gap-2">
              {!readOnly && <button type="button" class="btn btn-pri" disabled={!!busy || !form.name.trim() || !(logic ? form.result.trim() : form.hex.trim())} onClick={save}>{busy === "save" ? t("ex.saving") : t("ex.save")}</button>}
              {sel && (
                <button type="button" class="btn text-crit" disabled={!!busy} onClick={async () => {
                  setBusy("del");
                  try {
                    await actions.deleteExample(sel);
                    await load();
                    fresh();
                  } catch (e) {
                    setErr((e as Error).message);
                  } finally {
                    setBusy("");
                  }
                }}>{t("common.delete")}</button>
              )}
            </div>
          )}
          {res && (
            <section class={`rounded-lg border px-4 py-3 ${res.ok ? "border-good/40 bg-good-soft/40" : "border-warn/40 bg-warn-soft/40"}`}>
              <div class="flex items-center gap-2 text-[13px] font-semibold">
                <Icon name={res.ok ? "check" : "warn"} size={14} stroke={2.4} class={res.ok ? "text-good" : "text-warn"} />
                {res.ok ? t("ex.ok") : t("ex.notok")}
                <span class="font-normal text-muted">{t("ex.duration", { ms: fmtNumber(res.duration_ms, 2) })}</span>
              </div>
              {res.error && <p class="mb-0 mt-2 font-mono text-[12.5px] text-crit">{res.error}</p>}
              {(res.issues ?? []).map((i) => <p class="mb-0 mt-1 font-mono text-[12.5px] text-warn">{i}</p>)}
              {(res.diff ?? []).length > 0 && (
                <pre class="mb-0 mt-2 overflow-x-auto whitespace-pre-wrap font-mono text-[12px] text-ink2">{(res.diff ?? []).join("\n")}</pre>
              )}
              {res.geo && (
                <div class="mt-2 text-[12.5px] text-ink2">
                  <span class="font-semibold">{t("ex.geo")} </span>
                  {res.geo.positions.map((p) => <span class="font-mono">{p.key} {p.value.lat.toFixed(6)}, {p.value.lon.toFixed(6)}{p.value.accuracy_m !== undefined ? ` ±${p.value.accuracy_m} m` : ""}{p.value.source ? ` (${p.value.source})` : ""}; </span>)}
                  {([["ex.geo.wifi", res.geo.evidence.wifi], ["ex.geo.cells", res.geo.evidence.cells], ["ex.geo.ble", res.geo.evidence.ble]] as const).filter(([, l]) => (l as unknown[]).length > 0)
                    .map(([k, l]) => <span>{t(k, { count: (l as unknown[]).length })}; </span>)}
                  <details class="mt-1"><summary class="cursor-pointer text-muted">{t("ex.geo.show")}</summary>
                    <pre class="mb-0 mt-1 overflow-x-auto font-mono text-[12px]">{JSON.stringify(res.geo, null, 2)}</pre></details>
                </div>
              )}
              {!res.ok && res.got !== undefined && (
                <details class="mt-2 text-[12.5px]"><summary class="cursor-pointer text-muted">{t("ex.got")}</summary>
                  <pre class="mb-0 mt-1 overflow-x-auto font-mono text-[12px] text-ink2">{JSON.stringify(res.got, null, 2)}</pre></details>
              )}
            </section>
          )}
        </div>
      </section>
    </div>
  );
}
